import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { EMERGENCY_SITUATIONS } from "@/config/journeyCatalog";

export default function EmergencyPlanEditor({ value, contactsNote, onChange, onContactsNote, readOnly }) {
  const [open, setOpen] = useState({});
  const setField = (situation, k, v) =>
    onChange({ ...value, [situation]: { ...(value[situation] || {}), [k]: v } });

  return (
    <div className="space-y-3" data-testid="emergency-plan-editor">
      <p className="text-xs text-slate-500">
        Agreed actions for each situation. Escalation follows organisational procedures — do not treat non-emergency
        situations as requiring emergency services by default.
      </p>
      {EMERGENCY_SITUATIONS.map((s) => {
        const data = value[s.key] || {};
        const filled = Boolean(data.expected_action || data.who_to_contact);
        const isOpen = open[s.key];
        return (
          <div key={s.key} className={`rounded-md border ${filled ? "border-primary/40 bg-brand-light/30" : "border-border"}`} data-testid={`emergency-${s.key}`}>
            <button type="button" className="flex w-full items-center gap-2 px-3 py-2.5 text-sm font-medium text-left min-h-[44px]"
              data-testid={`emergency-toggle-${s.key}`} onClick={() => setOpen((o) => ({ ...o, [s.key]: !o[s.key] }))}>
              {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              <span className="flex-1">{s.label}</span>
              {filled && <span className="text-[10px] uppercase tracking-wide text-ukgreen font-bold">Recorded</span>}
            </button>
            {isOpen && (
              <div className="px-3 pb-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs">Expected action</Label>
                  <Textarea rows={2} data-testid={`emergency-action-${s.key}`} value={data.expected_action || ""} disabled={readOnly}
                    onChange={(e) => setField(s.key, "expected_action", e.target.value)} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Safe location</Label>
                  <Input value={data.safe_location || ""} disabled={readOnly} onChange={(e) => setField(s.key, "safe_location", e.target.value)} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Who to contact</Label>
                  <Input data-testid={`emergency-contact-${s.key}`} value={data.who_to_contact || ""} disabled={readOnly} onChange={(e) => setField(s.key, "who_to_contact", e.target.value)} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Backup contact</Label>
                  <Input value={data.backup_contact || ""} disabled={readOnly} onChange={(e) => setField(s.key, "backup_contact", e.target.value)} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Trainer guidance</Label>
                  <Input value={data.trainer_guidance || ""} disabled={readOnly} onChange={(e) => setField(s.key, "trainer_guidance", e.target.value)} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Parent/carer arrangement</Label>
                  <Input value={data.parent_arrangement || ""} disabled={readOnly} onChange={(e) => setField(s.key, "parent_arrangement", e.target.value)} /></div>
                <div className="space-y-1.5"><Label className="text-xs">School arrangement</Label>
                  <Input value={data.school_arrangement || ""} disabled={readOnly} onChange={(e) => setField(s.key, "school_arrangement", e.target.value)} /></div>
                <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs">Other instructions</Label>
                  <Input value={data.other || ""} disabled={readOnly} onChange={(e) => setField(s.key, "other", e.target.value)} /></div>
              </div>
            )}
          </div>
        );
      })}
      <div className="space-y-1.5 pt-2">
        <Label className="text-xs font-semibold">Emergency contacts summary (authorised contacts only)</Label>
        <Textarea rows={2} data-testid="emergency-contacts-note" value={contactsNote || ""} disabled={readOnly}
          onChange={(e) => onContactsNote(e.target.value)}
          placeholder="Parent/carer · Deluxe Travel Training office · Assigned trainer · Manager/duty contact · School — do not include unnecessary personal details" />
      </div>
    </div>
  );
}
